import React from 'react';
import { Table, ListBullets, Code, FileText, ArrowRight } from '@phosphor-icons/react';

/* ═══════════════════════════════════════════════════════
   OutputRenderer — Smart output type detection & display
   ═══════════════════════════════════════════════════════ */

/** Detect the output type */
function detectType(raw) {
  if (!raw) return 'empty';

  // If the raw value is already an object/array, it's JSON
  if (typeof raw === 'object') return 'json';

  const text = typeof raw === 'string' ? raw.trim() : String(raw).trim();

  // Try parsing as JSON
  if ((text.startsWith('{') || text.startsWith('[')) && text.length > 2) {
    try { JSON.parse(text); return 'json'; } catch {}
  }

  // Check for markdown patterns
  const mdPatterns = [
    /^#{1,3}\s+/m,             // # headings
    /\*\*[^*]+\*\*/,           // **bold**
    /^[-*]\s+/m,               // - bullet lists
    /^\d+\.\s+/m,              // 1. ordered lists
    /^>\s+/m,                  // > blockquotes
    /```/,                     // code blocks
    /\[.+\]\(.+\)/,           // [links](url)
  ];

  const mdScore = mdPatterns.filter(p => p.test(text)).length;
  if (mdScore >= 2) return 'markdown';

  // Check for section-based report format (UPPERCASE HEADINGS followed by content)
  const sectionPattern = /^[A-Z][A-Z\s&/]{3,}$/m;
  if (sectionPattern.test(text) && text.split('\n').length > 5) return 'report';

  return 'text';
}

/** Parse a JSON value into renderable key-value pairs */
function flattenForTable(obj, prefix = '') {
  const rows = [];
  for (const [key, val] of Object.entries(obj)) {
    const label = prefix ? `${prefix}.${key}` : key;
    if (val && typeof val === 'object' && !Array.isArray(val)) {
      rows.push(...flattenForTable(val, label));
    } else if (Array.isArray(val)) {
      rows.push({ key: label, value: val.map(v => typeof v === 'object' ? JSON.stringify(v) : String(v)).join(', ') });
    } else {
      rows.push({ key: label, value: val === null ? '—' : String(val) });
    }
  }
  return rows;
}

/** Render a JSON object/array as styled cards */
function JsonRenderer({ data }) {
  const parsed = typeof data === 'string' ? JSON.parse(data) : data;

  // Array of objects → render as a list of cards
  if (Array.isArray(parsed)) {
    return (
      <div className="space-y-2">
        {parsed.map((item, i) => (
          <div key={i} className="p-3 rounded-lg bg-[rgba(255,255,255,0.02)] border border-[rgba(255,255,255,0.08)]">
            {typeof item === 'object' && item !== null ? (
              <div className="space-y-1.5">
                {Object.entries(item).map(([k, v]) => (
                  <div key={k} className="flex gap-2 text-xs">
                    <span className="text-[#7D8896] font-medium min-w-[100px] shrink-0">{formatKey(k)}</span>
                    <span className="text-[#C8D1DB]">{typeof v === 'object' ? JSON.stringify(v) : String(v ?? '—')}</span>
                  </div>
                ))}
              </div>
            ) : (
              <span className="text-xs text-[#C8D1DB]">{String(item)}</span>
            )}
          </div>
        ))}
      </div>
    );
  }

  // Single object → render as key-value table
  if (typeof parsed === 'object' && parsed !== null) {
    const rows = flattenForTable(parsed);
    return (
      <div className="rounded-lg border border-[rgba(255,255,255,0.08)] overflow-hidden">
        {rows.map(({ key, value }, i) => (
          <div key={i} className={`flex gap-3 px-3 py-2 text-xs ${i % 2 === 0 ? 'bg-[rgba(255,255,255,0.01)]' : 'bg-[rgba(255,255,255,0.03)]'}`}>
            <span className="text-[#7D8896] font-medium min-w-[140px] shrink-0">{formatKey(key)}</span>
            <span className="text-[#C8D1DB] break-words">{value}</span>
          </div>
        ))}
      </div>
    );
  }

  return <span className="text-xs text-[#C8D1DB]">{String(parsed)}</span>;
}

/** Convert snake_case/camelCase keys to Title Case */
function formatKey(key) {
  return key
    .replace(/[._]/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\b\w/g, c => c.toUpperCase());
}

/** Render markdown-like text with formatting */
function MarkdownRenderer({ text }) {
  const lines = text.split('\n');
  const elements = [];
  let inCodeBlock = false;
  let codeBuffer = [];
  let listBuffer = [];
  let listType = null;

  const flushList = () => {
    if (listBuffer.length === 0) return;
    const Tag = listType === 'ol' ? 'ol' : 'ul';
    const cls = listType === 'ol'
      ? 'list-decimal list-inside space-y-1 text-xs text-[#C8D1DB] ml-2 mb-3'
      : 'list-disc list-inside space-y-1 text-xs text-[#C8D1DB] ml-2 mb-3';
    elements.push(
      <Tag key={`list-${elements.length}`} className={cls}>
        {listBuffer.map((item, j) => <li key={j} dangerouslySetInnerHTML={{ __html: inlineFormat(item) }} />)}
      </Tag>
    );
    listBuffer = [];
    listType = null;
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    // Code blocks
    if (line.startsWith('```')) {
      if (inCodeBlock) {
        elements.push(
          <pre key={`code-${i}`} className="text-xs text-[#C8D1DB] bg-[rgba(0,0,0,0.3)] rounded-lg p-3 mb-3 font-mono overflow-x-auto">
            {codeBuffer.join('\n')}
          </pre>
        );
        codeBuffer = [];
        inCodeBlock = false;
      } else {
        flushList();
        inCodeBlock = true;
      }
      continue;
    }
    if (inCodeBlock) { codeBuffer.push(line); continue; }

    // Headings
    const h3 = line.match(/^###\s+(.+)/);
    if (h3) { flushList(); elements.push(<h4 key={i} className="text-xs font-bold text-[#F0F4F8] mt-3 mb-1.5 flex items-center gap-1.5"><ArrowRight size={10} className="text-[#00E5C0]" />{h3[1]}</h4>); continue; }
    const h2 = line.match(/^##\s+(.+)/);
    if (h2) { flushList(); elements.push(<h3 key={i} className="text-sm font-bold text-[#F0F4F8] mt-4 mb-2 border-b border-[rgba(255,255,255,0.08)] pb-1.5">{h2[1]}</h3>); continue; }
    const h1 = line.match(/^#\s+(.+)/);
    if (h1) { flushList(); elements.push(<h2 key={i} className="text-base font-bold text-[#F0F4F8] mt-4 mb-2">{h1[1]}</h2>); continue; }

    // Blockquotes
    const bq = line.match(/^>\s+(.+)/);
    if (bq) { flushList(); elements.push(<blockquote key={i} className="border-l-2 border-[#00E5C0] pl-3 py-1 text-xs text-[#B0B8C4] italic mb-2 bg-[rgba(0,229,192,0.03)] rounded-r-lg">{bq[1]}</blockquote>); continue; }

    // Bullet lists
    const bullet = line.match(/^[-*]\s+(.+)/);
    if (bullet) { if (listType === 'ol') flushList(); listType = 'ul'; listBuffer.push(bullet[1]); continue; }

    // Ordered lists
    const ordered = line.match(/^\d+\.\s+(.+)/);
    if (ordered) { if (listType === 'ul') flushList(); listType = 'ol'; listBuffer.push(ordered[1]); continue; }

    // Horizontal rule
    if (/^[-*_]{3,}$/.test(line.trim())) { flushList(); elements.push(<hr key={i} className="border-[rgba(255,255,255,0.08)] my-3" />); continue; }

    // Empty lines
    if (!line.trim()) { flushList(); continue; }

    // UPPERCASE SECTION HEADERS (report-style)
    if (/^[A-Z][A-Z\s&/]{3,}$/.test(line.trim())) {
      flushList();
      elements.push(<h3 key={i} className="text-xs font-bold text-[#00E5C0] uppercase tracking-wider mt-4 mb-2 flex items-center gap-1.5"><FileText size={11} />{line.trim()}</h3>);
      continue;
    }

    // Normal paragraph
    flushList();
    elements.push(<p key={i} className="text-xs text-[#C8D1DB] mb-1.5 leading-relaxed" dangerouslySetInnerHTML={{ __html: inlineFormat(line) }} />);
  }

  flushList();
  return <div className="space-y-0">{elements}</div>;
}

/** Format inline markdown: **bold**, *italic*, `code`, [link](url) */
function inlineFormat(text) {
  return text
    .replace(/`([^`]+)`/g, '<code class="px-1 py-0.5 rounded bg-[rgba(255,255,255,0.06)] text-[#00E5C0] font-mono text-[11px]">$1</code>')
    .replace(/\*\*([^*]+)\*\*/g, '<strong class="text-[#F0F4F8] font-semibold">$1</strong>')
    .replace(/\*([^*]+)\*/g, '<em class="text-[#B0B8C4]">$1</em>')
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" class="text-[#00E5C0] underline" target="_blank" rel="noopener">$1</a>');
}

/** Main OutputRenderer component */
const OutputRenderer = ({ output, maxHeight = 320 }) => {
  if (!output) return null;

  const type = detectType(output);
  const text = typeof output === 'string' ? output : JSON.stringify(output, null, 2);

  const typeLabel = { json: 'Structured Data', markdown: 'Report', report: 'Report', text: 'Output' }[type] || 'Output';
  const TypeIcon = { json: Table, markdown: FileText, report: FileText, text: Code }[type] || Code;

  return (
    <div data-testid="output-renderer">
      {/* Type indicator badge */}
      <div className="flex items-center gap-2 mb-2.5">
        <span className="text-[10px] font-semibold text-[#7D8896] uppercase tracking-wider flex items-center gap-1.5">
          <TypeIcon size={10} className="text-[#00E5C0]" weight="fill" /> {typeLabel}
        </span>
        <span className="text-[9px] px-1.5 py-0.5 rounded bg-[rgba(255,255,255,0.04)] text-[#7D8896] border border-[rgba(255,255,255,0.08)]">
          {type}
        </span>
      </div>

      {/* Rendered content */}
      <div className={`overflow-y-auto scrollbar-thin scrollbar-thumb-[rgba(255,255,255,0.08)] scrollbar-track-transparent`} style={{ maxHeight }}>
        {type === 'json' ? (
          <JsonRenderer data={output} />
        ) : type === 'markdown' || type === 'report' ? (
          <MarkdownRenderer text={text} />
        ) : (
          <pre className="text-xs text-[#C8D1DB] whitespace-pre-wrap break-words font-mono leading-relaxed">
            {text}
          </pre>
        )}
      </div>
    </div>
  );
};

export { OutputRenderer, detectType };
export default OutputRenderer;
